// src/utils/metadata.js
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../config/firebase';

const YOUTUBE_API_KEY = 'AIzaSyCJ7O3nfS4XczbbqiXQYS1qgPn81T50N9o';

const isCompleteUrl = (url) => {
  try {
    const urlObj = new URL(url);
    return (
      (urlObj.protocol === 'http:' || urlObj.protocol === 'https:') &&
      urlObj.hostname.includes('.') &&
      urlObj.hostname.split('.').every(part => part.length > 0) &&
      urlObj.hostname.split('.').pop().length >= 2
    );
  } catch (e) {
    return false;
  }
};

const getDomainFromUrl = (url) => {
  try {
    return new URL(url).hostname;
  } catch (e) {
    return null;
  }
};

const getFaviconUrl = (url) => {
  const domain = getDomainFromUrl(url);
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
};

const getYouTubeVideoId = (url) => {
  try {
    const urlObj = new URL(url);
    if (urlObj.hostname.includes('youtube.com')) {
      return urlObj.searchParams.get('v');
    } else if (urlObj.hostname.includes('youtu.be')) {
      return urlObj.pathname.slice(1);
    }
  } catch (e) {
    return null;
  }
  return null;
};

const isYouTubeUrl = (url) => {
  try {
    const domain = getDomainFromUrl(url);
    return domain.includes('youtube.com') || domain.includes('youtu.be');
  } catch (e) {
    return false;
  }
};

export const validateImageUrl = async (url, options = {}) => {
  try {
    const response = await fetch(url, { 
      method: 'HEAD',
      mode: 'no-cors',
      ...options 
    });
    return true;
  } catch (e) {
    console.error('Image validation error:', e);
    return false;
  }
};

const getYoutubeMetadata = (url) => {
  const videoId = getYouTubeVideoId(url);
  if (!videoId) return null;

  // Array van mogelijke YouTube thumbnail kwaliteiten
  const thumbnailQualities = [
    'maxresdefault',  // 1920x1080
    'sddefault',      // 640x480
    'hqdefault',      // 480x360
    'mqdefault',      // 320x180
    'default'         // 120x90
  ];

  return {
    title: "YouTube Video",
    description: "Bekijk deze video op YouTube",
    images: thumbnailQualities.map(quality => ({
      url: `https://img.youtube.com/vi/${videoId}/${quality}.jpg`,
      quality
    })),
    favicon: getFaviconUrl(url),
    url,
    isYoutube: true,
    videoId
  };
};

const fetchYouTubeMetadata = async (videoId) => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${videoId}&key=${YOUTUBE_API_KEY}`
    );
    
    const data = await response.json();
    
    if (data.items && data.items[0]) {
      const video = data.items[0].snippet;
      const thumbnailQualities = [
        'maxres',
        'standard',
        'high',
        'medium',
        'default'
      ];

      const images = thumbnailQualities
        .filter(quality => video.thumbnails[quality])
        .map(quality => ({
          url: video.thumbnails[quality].url,
          quality,
          width: video.thumbnails[quality].width,
          height: video.thumbnails[quality].height
        }));

      return {
        title: video.title,
        description: video.description,
        images,
        favicon: getFaviconUrl(`https://youtube.com/watch?v=${videoId}`),
        url: `https://youtube.com/watch?v=${videoId}`,
        isYoutube: true,
        videoId,
        channelTitle: video.channelTitle,
        publishedAt: video.publishedAt
      };
    }
    
    return getYoutubeMetadata(`https://youtube.com/watch?v=${videoId}`);
  } catch (error) {
    console.error('Error fetching YouTube metadata:', error);
    return getYoutubeMetadata(`https://youtube.com/watch?v=${videoId}`);
  }
};

export const extractMetadata = async (url) => {
  if (!isCompleteUrl(url)) {
    return null;
  }

  try {
    // Check voor YouTube URLs
    if (isYouTubeUrl(url)) {
      const videoId = getYouTubeVideoId(url);
      if (videoId) {
        // Probeer eerst de API metadata
        try {
          const apiMetadata = await fetchYouTubeMetadata(videoId);
          if (apiMetadata) {
            return apiMetadata;
          }
        } catch (error) {
          console.error('YouTube API error:', error);
        }

        // Fallback naar basis metadata
        return getYoutubeMetadata(url);
      }
    }

    // Voor niet-YouTube URLs
    const functions = getFunctions(app);
    const getMetadata = httpsCallable(functions, 'extractMetadata');
    
    const result = await getMetadata({ url });
    const metadata = {
      ...result.data,
      images: result.data.image ? [{ url: result.data.image }] : [],
      favicon: getFaviconUrl(url),
      url
    };

    if (!metadata.title) {
      metadata.title = getDomainFromUrl(url) || 'Untitled';
    }
    if (!metadata.description) {
      metadata.description = 'Geen beschrijving beschikbaar';
    }

    return metadata;

  } catch (error) {
    console.error('Error extracting metadata:', error);
    
    return {
      title: getDomainFromUrl(url) || 'Kon metadata niet laden',
      description: 'Er kon geen metadata worden opgehaald voor deze URL.',
      images: [],
      favicon: getFaviconUrl(url),
      url
    };
  }
};